import { css, html, LitElement } from 'lit';
import { navigator as nav } from 'lit-element-router';

import { RequesterMixin } from '@brightspace-ui/core/mixins/provider-mixin';

import '@brightspace-ui/core/components/button/button-subtle';
import '@brightspace-ui/core/components/button/button-icon';
import '@brightspace-ui/core/components/collapsible-panel/collapsible-panel.js';
import { heading1Styles } from '@brightspace-ui/core/components/typography/styles';
import { inputLabelStyles } from '@brightspace-ui/core/components/inputs/input-label-styles.js';
import { inputStyles } from '@brightspace-ui/core/components/inputs/input-styles.js';
import { selectStyles } from '@brightspace-ui/core/components/inputs/input-select-styles.js';

import '../components/environment.js';
import { getServiceStatusIndicator, inputTemplate } from '../helpers/helpers.js';

const environmentFormAttributes = [
  { id: 'id', label: 'Stage name', required: true, disabled: () => false },
  { id: 'displayName', label: 'Display name', required: true, disabled: () => false },
];

class Environments extends RequesterMixin(nav(LitElement)) {

  static get properties() {
    return {
      _environments: { type: Array },
      _expandedPanels: { type: Object },
      _refreshingEnvironments: { type: Object },
    };
  }

  static get styles() {
    return [
      heading1Styles,
      inputLabelStyles,
      inputStyles,
      selectStyles,
      css`
        :host {
          display: block;
        }

        .environment {
          margin: 10px 0;
        }

        .add-environment-button {
          display: inline-block;
        }

        .environments-header {
          display: inline-block;
        }

        .header {
          align-items: center;
          display: flex;
        }

        .env-header {
          font-weight: bold;
          padding-top: 10px;
        }

        .deploy-date {
          font-size: 0.8em;
        }

        .spacer {
          flex-grow: 1;
        }

        ::slotted([slot=header]) {
          grid-row: header;
        }

        ::slotted([slot=content]) {
          grid-row: content;
        }

        d2l-collapsible-panel-summary-item {
          display: flex;
          justify-content: space-between;
        }

        .deploy-date d2l-button-icon {
          --d2l-button-icon-min-height: 1.5rem;
          --d2l-button-icon-min-width: 1.5rem;
        }
      `];
  }

  constructor() {
    super();
    this._currentEnvironment = { tenants: [] };
    this._environments = [];

    this._expandedPanels = {};
    this._refreshingEnvironments = {};
  }

  connectedCallback() {
    super.connectedCallback();
    this.client = this.requestInstance('d2l-pm-client');
  }

  async firstUpdated() {
    this._environments = await this.client.fetchEnvironments();
    this._environments.sort((a, b) => {
      return a.displayName.localeCompare(b.displayName);
    });

    // Auto-refresh status for environments without cached status
    this._refreshMissingStatuses();
  }

  async _refreshMissingStatuses() {
    // Find environments missing Wave or FUSS status
    const environmentsWithoutStatus = this._environments.filter(env => !env.status || !env.fussHealth);
    if (environmentsWithoutStatus.length === 0) return;

    // Refresh each environment without status (don't await - let them run in background)
    for (const env of environmentsWithoutStatus) {
      this._refreshEnvironmentStatusAsync(env.id);
    }
  }

  async _refreshEnvironmentStatusAsync(environmentId) {
    // Mark as refreshing
    this._refreshingEnvironments = {
      ...this._refreshingEnvironments,
      [environmentId]: true,
    };

    try {
      const result = await this.client.refreshEnvironmentStatus(environmentId);

      // Update the environment with new status (Wave + FUSS)
      this._environments = this._environments.map(env =>
        (env.id === environmentId ? {
          ...env,
          status: result.status,
          statusCheckedAt: result.statusCheckedAt,
          fussHealth: result.fussHealth,
          fussHealthCheckedAt: result.fussHealthCheckedAt,
        } : env)
      );
    } catch (err) {
      // On error, set status to error for both services
      const errorStatus = { status: 'error', message: err.message || 'Refresh failed' };
      this._environments = this._environments.map(env =>
        (env.id === environmentId ? { ...env, status: errorStatus, fussHealth: errorStatus } : env)
      );
    } finally {
      // Mark as not refreshing
      this._refreshingEnvironments = {
        ...this._refreshingEnvironments,
        [environmentId]: false,
      };
    }
  }

  get environmentDialog() {
    if (!this._environmentDialog) this._environmentDialog = this.shadowRoot.getElementById('show-environment-dialog');
    return this._environmentDialog;
  }

  _openAddEnvironmentDialog() {
    this.environmentDialog.opened = true;
  }

  _changeValue(base) {
    return e => {
      if (e.target.id) {
        const splitId = e.target.id.split('_')[0];
        base[splitId] = e.target.value;
        this.update();
      }
    };
  }

  async _submitEnvironmentForm() {
    const environment = await this.client.createEnvironment(this._currentEnvironment);
    if (!environment) return;
    this._environments = [...this._environments, environment];
  }

  _addEnvironmentDialogTemplate() {
    return html`
      <d2l-dialog id="show-environment-dialog" title-text="Create environment">
        <d2l-form id="environmentForm" @change="${this._changeValue(this._currentEnvironment)}">
          ${environmentFormAttributes.map(attr => inputTemplate(attr, this._currentEnvironment))}
        </d2l-form>
        <d2l-button slot="footer" @click="${this._submitEnvironmentForm}" primary data-dialog-action="done">Submit</d2l-button>
        <d2l-button slot="footer" data-dialog-action>Cancel</d2l-button>
      </d2l-dialog>`;
  }

  _viewDeployments(id) {
    return () => {
      this.navigate(`/deployments/${id}`);
    };
  }

  _handleExpand(id) {
    return () => {
      this._expandedPanels = {
        ...this._expandedPanels,
        [id]: true, // Mark the panel as expanded
      };
    };
  }

  _handleCollapse(id) {
    return () => {
      this._expandedPanels = {
        ...this._expandedPanels,
        [id]: false, // Mark the panel as collapsed
      };
    };
  }

  _navigateToEnvironment(e) {
    e.preventDefault();
    e.stopPropagation();
    const id = e.currentTarget.dataset.environmentId;
    window.open(`/stage/${id}`, '_blank');
  }

  _refreshEnvironmentStatus(environmentId) {
    return (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Set status to checking while we refresh (both Wave and FUSS)
      this._environments = this._environments.map(env =>
        (env.id === environmentId ? { ...env, status: null, fussHealth: null } : env)
      );

      this._refreshEnvironmentStatusAsync(environmentId);
    };
  }

  render() {
    return html`
      <div class="header">
        <h1 class="d2l-heading-1 environments-header">Instances - </h1>
        <d2l-button-subtle
          @click="${this._openAddEnvironmentDialog}"
          icon="tier1:add"
          text="Add environment">
        </d2l-button-subtle>
      </div>
      ${this._environments.map(environment => html`
        <d2l-collapsible-panel
          panel-title=${environment.displayName}
          class="environment"
          @d2l-collapsible-panel-expand="${this._handleExpand(environment.id)}"
          @d2l-collapsible-panel-collapse="${this._handleCollapse(environment.id)}">
          <div slot="header">
            <span class="deploy-date" style="padding-top: 5px; display: flex; align-items: center; gap: 0.5rem;">
              ${getServiceStatusIndicator(environment.id, 'Wave', environment.status)}
              ${getServiceStatusIndicator(environment.id, 'FUSS', environment.fussHealth)}
              <d2l-button-icon
                text="Refresh status"
                icon="tier1:refresh"
                ?disabled="${this._refreshingEnvironments[environment.id]}"
                @click="${this._refreshEnvironmentStatus(environment.id)}">
              </d2l-button-icon>
            </span>
          </div>
          <div slot="actions">
            <d2l-button-subtle
              text="View details"
              icon="tier1:new-window"
              icon-right
              data-environment-id="${environment.id}"
              @click="${this._navigateToEnvironment}">
            </d2l-button-subtle>
          </div>
          ${this._expandedPanels[environment.id] ? html`<pm-environment class="environment" environment-id="${environment.id}"></pm-environment>` : ''}
        </d2l-collapsible-panel>
      `)}
      ${this._addEnvironmentDialogTemplate()}
    `;
  }
}

window.customElements.define('pm-environments', Environments);
